import { Reveal } from "../components/Reveal";
import { useLang } from "../i18n";
import { IMAGES } from "../data/products";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { toast } from "sonner";

export default function ArabianPage() {
    const { t } = useLang();
    const imgRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

    const CHAPTERS = [
        { n: "01", title: t("arabianStory.c1t"), text: t("arabianStory.c1x") },
        { n: "02", title: t("arabianStory.c2t"), text: t("arabianStory.c2x") },
        { n: "03", title: t("arabianStory.c3t"), text: t("arabianStory.c3x") },
    ];

    return (
        <div className="bg-charcoal" data-testid="arabian-page">
            <section ref={imgRef} className="relative min-h-[85vh] flex items-center justify-center overflow-hidden px-6" data-testid="arabian-page-hero">
                <motion.div style={{ y, scale: 1.1 }} className="absolute inset-0">
                    <img src={IMAGES.arabianScenery} alt="The golden heritage of Arabia" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-charcoal/70" />
                </motion.div>
                <div className="relative z-10 text-center max-w-3xl mx-auto pt-24">
                    <Reveal>
                        <p className="text-xs tracking-[0.35em] uppercase text-gold mb-8">{t("arabian.overline")}</p>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight text-ivory mb-6" data-testid="arabian-page-heading">
                            {t("arabian.heading")}
                        </h1>
                        <p className="font-serif italic text-2xl sm:text-3xl text-gold/80 mb-6" data-testid="arabian-page-coming-soon">
                            {t("arabian.cs")}
                        </p>
                        <p className="text-ivory/60 text-sm sm:text-base leading-relaxed max-w-md mx-auto mb-12">{t("arabian.body")}</p>
                        <button
                            data-testid="arabian-notify-button"
                            onClick={() => toast.success(t("toast.noted"), { description: t("toast.followIg") })}
                            className="border border-gold text-gold px-10 py-4 text-xs tracking-[0.25em] uppercase hover:bg-gold hover:text-charcoal transition-colors duration-300"
                        >
                            {t("arabian.notify")}
                        </button>
                    </Reveal>
                </div>
            </section>

            <section className="py-28 sm:py-40 px-6 sm:px-10" data-testid="arabian-heritage-section">
                <div className="max-w-7xl mx-auto">
                    <Reveal>
                        <p className="text-xs tracking-[0.35em] uppercase text-gold mb-6">{t("arabianStory.overline")}</p>
                        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight text-ivory mb-6" data-testid="arabian-heritage-heading">
                            {t("arabianStory.heading")}
                        </h2>
                        <p className="text-ivory/50 text-sm sm:text-base max-w-2xl leading-relaxed mb-20">{t("arabianStory.intro")}</p>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gold/20 border border-gold/20">
                        {CHAPTERS.map((c, i) => (
                            <Reveal key={c.n} delay={i * 0.15} className="bg-charcoal">
                                <div className="p-10 sm:p-12 h-full group hover:bg-[#221F19] transition-colors duration-500" data-testid={`arabian-heritage-chapter-${c.n}`}>
                                    <span className="font-serif text-4xl text-gold/40 group-hover:text-gold/70 transition-colors duration-500">{c.n}</span>
                                    <h3 className="font-serif text-2xl tracking-tight text-ivory mt-10 mb-4">{c.title}</h3>
                                    <p className="text-sm text-ivory/50 leading-relaxed">{c.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
